(() => {
  'use strict';
  const MAX_PRODUCTS = 48;
  const FAMILY_ORDER = ['Wassen', 'Vlekken behandelen', 'Drogen & ophangen', 'Strijken', 'Overige artikelen'];
  const normalizeFamily = name => FAMILY_ORDER.includes(name) ? name : 'Overige artikelen';
  // Public fragment identifiers do not depend on display order or stock.
  const FAMILY_ANCHORS = {
    'Wassen': ['wassen', 'oww-wasgoed-family-0'],
    'Vlekken behandelen': ['vlekken-behandelen', 'oww-wasgoed-family-1'],
    'Drogen & ophangen': ['drogen-ophangen', 'oww-wasgoed-family-2'],
    'Strijken': ['strijken', 'oww-wasgoed-family-3'],
    'Overige artikelen': ['oww-wasgoed-family-4', 'oww-wasgoed-family-4']
  };
  const headingId = name => FAMILY_ANCHORS[normalizeFamily(name)][0];
  const legacyHeadingId = name => FAMILY_ANCHORS[normalizeFamily(name)][1];
  const complete = (items, expected) => Number.isInteger(expected) && expected > 0 && expected <= MAX_PRODUCTS && items.length === expected && new Set(items.map(item => String(item.id))).size === expected;
  function arrange(items, { family = '', availableOnly = false, sort = 'recommended' } = {}) {
    const visible = items.filter(item => (!family || normalizeFamily(item.family) === family) && (!availableOnly || item.available));
    if (sort !== 'recommended') {
      const compare = sort === 'price-ascending' ? (a, b) => a.price - b.price : sort === 'price-descending' ? (a, b) => b.price - a.price : (a, b) => a.title.localeCompare(b.title, 'nl', { sensitivity: 'base', numeric: true });
      return { items: visible.slice().sort(compare), groups: [] };
    }
    const groups = FAMILY_ORDER.map(name => {
      const members = visible.filter(item => normalizeFamily(item.family) === name);
      return { name, available: members.some(item => item.available), items: members.filter(item => item.available).concat(members.filter(item => !item.available)) };
    }).filter(group => group.items.length);
    // Unassigned products remain visible at the end.
    const main = groups.filter(group => !['Overige artikelen'].includes(group.name));
    const ordered = main.filter(group => group.available).concat(main.filter(group => !group.available), groups.filter(group => ['Overige artikelen'].includes(group.name)));
    return { items: ordered.flatMap(group => group.items), groups: ordered };
  }
  const familyFromFragment = hash => FAMILY_ORDER.find(name => FAMILY_ANCHORS[name].includes(String(hash).replace(/^#/, ''))) || '';
  if (typeof module !== 'undefined' && module.exports) module.exports = { arrange, complete, normalizeFamily, headingId, legacyHeadingId, familyFromFragment, FAMILY_ORDER, MAX_PRODUCTS };
  if (typeof customElements === 'undefined' || customElements.get('oww-wasgoed-preview')) return;
  customElements.define('oww-wasgoed-preview', class extends HTMLElement {
    connectedCallback() { queueMicrotask(() => this.initialize()); }
    disconnectedCallback() { this.events?.abort(); this.initialized = false; }
    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.grid = this.querySelector('#oww-wasgoed-grid');
      this.controls = this.querySelector('[data-wasgoed-controls]');
      if (!this.grid || !this.controls) return;
      this.items = Array.from(this.grid.querySelectorAll('[data-wasgoed-id]')).map(node => ({ node, id: node.dataset.wasgoedId, family: normalizeFamily(node.dataset.family), available: node.dataset.available === 'true', price: Number(node.dataset.price), title: node.dataset.title }));
      if (!complete(this.items, Number(this.dataset.expected))) return;
      this.initialized = true;
      this.family = '';
      this.stock = this.controls.querySelector('[data-wasgoed-stock]');
      this.sort = this.controls.querySelector('[data-wasgoed-sort]');
      this.nav = this.controls.querySelector('[data-wasgoed-links]');
      this.status = this.controls.querySelector('[data-wasgoed-status]');
      this.empty = this.querySelector('[data-wasgoed-empty]');
      this.explanation = this.controls.querySelector('[data-wasgoed-explanation]');
      const present = new Set(this.items.map(item => item.family));
      this.presentFamilies = FAMILY_ORDER.filter(name => present.has(name));
      this.events = new AbortController();
      [this.stock, this.sort].forEach(control => control.addEventListener('change', () => this.render(), { signal: this.events.signal }));
      this.nav.addEventListener('click', event => {
        const button = event.target.closest('button[data-family]');
        if (!button) return;
        this.family = button.dataset.family;
        if (!this.family) { this.stock.checked = false; this.sort.value = 'recommended'; }
        this.render();
        const url = new URL(location.href); url.hash = this.family ? headingId(this.family) : '';
        history.replaceState(history.state, '', url);
        requestAnimationFrame(() => {
          this.status.tabIndex = -1;
          this.status.focus({ preventScroll: true });
          this.status.scrollIntoView({ block: 'start', behavior: 'instant' });
        });
      }, { signal: this.events.signal });
      const applyFragment = (scroll = true) => {
        const family = familyFromFragment(location.hash);
        if (family && this.presentFamilies.includes(family)) this.revealFamily(family, scroll);
      };
      window.addEventListener('hashchange', () => applyFragment(), { signal: this.events.signal });
      this.addEventListener('oww:family-anchor', event => {
        const family = familyFromFragment(event.detail?.hash);
        if (family) this.revealFamily(family, false);
      }, { signal: this.events.signal });
      this.controls.hidden = false;
      this.render();
      // On browser Back, restore the selection without overriding native scroll restoration.
      applyFragment(window.performance?.getEntriesByType('navigation')[0]?.type !== 'back_forward');
    }
    revealFamily(family, scroll) {
      this.family = family; this.stock.checked = false; this.sort.value = 'recommended'; this.render();
      if (scroll) requestAnimationFrame(() => {
        const target = document.getElementById(headingId(family));
        target?.focus({ preventScroll: true }); target?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    }

    render() {
      const result = arrange(this.items, { family: this.family, availableOnly: this.stock.checked, sort: this.sort.value });
      this.grid.querySelectorAll('[data-wasgoed-heading]').forEach(node => node.remove());
      const visible = new Set(result.items);
      this.items.forEach(item => item.node.hidden = !visible.has(item));
      const fragment = document.createDocumentFragment();
      if (this.sort.value === 'recommended') {
        result.groups.forEach(group => {
          const li = document.createElement('li'); li.className = 'oww-wasgoed-heading'; li.dataset.wasgoedHeading = '';
          if (legacyHeadingId(group.name) !== headingId(group.name)) li.id = legacyHeadingId(group.name);
          const h = document.createElement('h2'); h.id = headingId(group.name); h.tabIndex = -1; h.textContent = group.name; li.append(h);
          if (!group.available) { const p = document.createElement('p'); p.textContent = 'Deze artikelen zijn momenteel uitverkocht.'; li.append(p); }
          fragment.append(li); group.items.forEach(item => fragment.append(item.node));
        });
      } else {
        if (this.family) { const li = document.createElement('li'); li.className = 'oww-wasgoed-heading'; li.dataset.wasgoedHeading = ''; if (legacyHeadingId(this.family) !== headingId(this.family)) li.id = legacyHeadingId(this.family); const h = document.createElement('h2'); h.id = headingId(this.family); h.textContent = this.family; h.tabIndex = -1; li.append(h); fragment.append(li); }
        result.items.forEach(item => fragment.append(item.node));
      }
      this.items.filter(item => !visible.has(item)).forEach(item => fragment.append(item.node));
      this.grid.append(fragment);
      if (!this.nav.children.length) {
        ['', ...this.presentFamilies].forEach(name => {
          const button = document.createElement('button'); button.type = 'button'; button.dataset.family = name;
          button.textContent = name || 'Alles tonen';
          if (!name) button.dataset.wasgoedReset = '';
          this.nav.append(button);
        });
      }
      this.nav.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.family === this.family)));
      this.nav.hidden = false;
      this.status.textContent = `${result.items.length} van ${this.items.length} artikelen`;
      this.empty.hidden = result.items.length !== 0;
      this.explanation.hidden = this.sort.value !== 'recommended';
    }
  });
})();
